import { Ref, watch } from '@nuxtjs/composition-api';
import aa from 'search-insights';

import { cappedObjectIds } from '../helpers/cappedObjectIds';
import { modifyAlgoliaCookieDomain } from '../helpers/modifyAlgoliaCookieDomain';
import { calculateDiscount, sumCartItems } from '../helpers/sumCartItems';
import { transformFilterKeyVal } from '../helpers/transformFilterKeyVal';
import {
  EventFilterClickedParams,
  EventProductAddedToCardParams,
  EventProductClickedParams,
} from './types';

const SEARCH_INSIGHTS_INDEX = 'shopify_products';

interface AlgoliaEventsParams {
  apiKey: string;
  appId: string;
  authenticatedUserToken: Ref<string | null>;
}

// eslint-disable-next-line max-lines-per-function
const useAlgoliaEvents = ({ apiKey, appId, authenticatedUserToken }: AlgoliaEventsParams) => {
  const initSearchInsights = () => {
    aa('init', {
      apiKey,
      appId,
      useCookie: true,
    });
    modifyAlgoliaCookieDomain().catch(console.error);
  };

  const eventPLPViewed = (objectIDs: string[]) => {
    aa('viewedObjectIDs', {
      index: SEARCH_INSIGHTS_INDEX,
      eventName: 'Search Hits Viewed',
      objectIDs: cappedObjectIds(objectIDs),
    }).catch(console.error);
  };

  const eventPDPViewed = (objectID: string) => {
    aa('viewedObjectIDs', {
      index: SEARCH_INSIGHTS_INDEX,
      eventName: 'Product Viewed',
      objectIDs: [objectID],
    }).catch(console.error);
  };

  const eventFilterViewed = ({ filters }: EventFilterClickedParams) => {
    if (!Object.keys(filters).length) return;
    aa('viewedFilters', {
      index: SEARCH_INSIGHTS_INDEX,
      eventName: 'PLP: Filter Viewed',
      filters: transformFilterKeyVal(filters),
    }).catch(console.error);
  };

  const eventFilterClicked = ({ filters }: EventFilterClickedParams) => {
    if (!Object.keys(filters).length) return;
    aa('clickedFilters', {
      index: SEARCH_INSIGHTS_INDEX,
      eventName: 'PLP: Filter Clicked',
      filters: transformFilterKeyVal(filters),
    }).catch(console.error);
  };

  const eventProductClicked = ({ queryId, objectId, position }: EventProductClickedParams) => {
    aa('clickedObjectIDsAfterSearch', {
      index: SEARCH_INSIGHTS_INDEX,
      eventName: `Product Card Clicked`,
      queryID: queryId,
      objectIDs: [objectId],
      positions: [position],
    }).catch(console.error);
  };

  const eventProductAddedToCart = (params: EventProductAddedToCardParams) => {
    const { source, queryId, objectId, price, quantity, compareToPrice } = params;
    const discount = calculateDiscount(price, compareToPrice);
    const objectData = [{ price, quantity, discount }];
    const payload = {
      index: SEARCH_INSIGHTS_INDEX,
      objectIDs: [objectId],
      objectData,
      value: sumCartItems(objectData),
      currency: 'AUD',
    };

    if (queryId) {
      aa('addedToCartObjectIDsAfterSearch', {
        ...payload,
        queryID: queryId,
        eventName: `${source}: Product Added to Cart after Search`,
      }).catch(console.error);
      aa('convertedObjectIDsAfterSearch', {
        ...payload,
        queryID: queryId,
        eventName: `${source}: Product Converted After Search`,
      }).catch(console.error);
    } else {
      aa('addedToCartObjectIDs', {
        ...payload,
        eventName: `${source}: Product Added to Cart`,
      }).catch(console.error);
      aa('convertedObjectIDs', {
        ...payload,
        eventName: `${source}: Product Converted`,
      }).catch(console.error);
    }
  };

  watch(authenticatedUserToken, (authenticatedUserToken) => {
    if (authenticatedUserToken) {
      aa('setAuthenticatedUserToken', authenticatedUserToken);
    }
  });

  return {
    initSearchInsights,
    eventPLPViewed,
    eventPDPViewed,
    eventFilterViewed,
    eventFilterClicked,
    eventProductClicked,
    eventProductAddedToCart,
  };
};

export default useAlgoliaEvents;
